import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Stack, Typography, Link } from '@mui/material';
import {
  Email,
  MessageBubblesIcon,
  NoMatchBanner,
  WhatsAppIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { useAppDispatch } from '../../store/hooks';
import { resetFlashMessage, updateFlashMessage } from '../../store/actions/flashMessage';
import { useGetMyProfileQuery } from '../../services/users';
import { CircularProgress } from '@classity-edtech/ui-base';
import { Roles } from '@classity-edtech/platform-utils';

export interface PendingStateProps extends MuiBoxProps {}

export const PendingState = ({ className }: PendingStateProps) => {
  const { data: user, isLoading: loadingUser, isFetching: fetchingUser } = useGetMyProfileQuery();

  const dispatch: any = useAppDispatch();
  const email = 'support@classity.co';
  const phone = '0806 595 7778';

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);

      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'success',
          message: 'Text copied',
        }),
      );

      setTimeout(() => dispatch(resetFlashMessage()), 7000);
    } catch (err) {
      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Failed to copy text',
        }),
      );

      setTimeout(() => dispatch(resetFlashMessage()), 7000);
    }
  };

  return (
    <Stack
      sx={{
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: { xs: '100%', sm: '410px' },
        mx: 'auto',
        mt: 5,
      }}
    >
      {loadingUser || fetchingUser ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {user.roleId === Roles.TUTOR && (
            <>
              <NoMatchBanner sx={{ width: '170px', height: '130px' }} />
              <Typography variant='subtitle5'>Congratulations, let's get started</Typography>

              <Typography variant='body3'>
                Create lessons on your integrated Google Calendar. You can review the short help
                video below.
              </Typography>
            </>
          )}
          {user.roleId === Roles.PROSPECT_TUTOR && (
            <>
              <MessageBubblesIcon sx={{ width: '170px', height: '130px' }} />

              <Typography variant='subtitle5'>Your application is under review</Typography>

              <Typography variant='body3'>
                Thank you for applying for the instructor position. Your application is currently
                under review; we will notify you of any updates or next steps.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1,
                    gap: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => copyText(email)}
                >
                  <Email sx={{ width: '16px', height: '16px' }} />
                  <Typography variant='body3'>{email}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1,
                    gap: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => copyText(phone)}
                >
                  <WhatsAppIcon sx={{ width: '16px', height: '16px' }} />
                  <Typography variant='body3'>{phone}</Typography>
                </Box>
              </Box>
            </>
          )}
          {user.roleId === Roles.STUDENT && (
            <>
              <MessageBubblesIcon sx={{ width: '170px', height: '130px' }} />
              <Typography variant='subtitle5'>
                You're confirmed! Thanks for joining the waitlist
              </Typography>

              <Typography variant='body3'>
                While we prep your dashboard, you can follow our journey and get free learning tips
                on our{' '}
                <Link
                  href='https://tiktok.com/@classityco'
                  target='_blank'
                  underline='hover'
                  variant='inherit'
                >
                  Tiktok
                </Link>{' '}
                pages. You can also reach out to us below if you have any questions.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1,
                    gap: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => copyText(email)}
                >
                  <Email sx={{ width: '16px', height: '16px' }} />
                  <Typography variant='body3'>{email}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1,
                    gap: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => copyText(phone)}
                >
                  <WhatsAppIcon sx={{ width: '16px', height: '16px' }} />
                  <Typography variant='body3'>{phone}</Typography>
                </Box>
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

PendingState.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

PendingState.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(PendingState)(
  ({ theme }) => `
    & .MuiPaper-root {
    }
  `,
);
